// ═══════════════════════════════════════════════════════════
// Timeline Tab — immutable audit log for a request
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { formatDateTime } from '../../../../lib/format';
import { IconLoader2, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { listAuditLog, ACTION_LABELS, type AuditEntry } from '../../../../api/auditLog';
import { FIELDS } from '../../../../api/fields';

interface Props { requestId: number }

// ── Reverse map: GAB field ID → human label ────────────────
const F = FIELDS.requests;
const FIELD_LABELS: Record<string, string> = {
  [F.status]:          'Status',
  [F.assignedUserId]:  'Assigned to',
  [F.assignedDeptId]:  'Department',
  [F.subject]:         'Subject',
  [F.description]:     'Description',
  [F.priority]:        'Priority',
  [F.requestChannel]:  'Channel',
  [F.internalNotes]:   'Internal notes',
  [F.closureReason]:   'Closure reason',
  [F.extensionReason]: 'Extension reason',
  [F.clockPausedReason]: 'Pause reason',
  [F.requestType]:     'Request type',
  [F.tags]:            'Tags',
};

const STATUS_LABELS: Record<string, string> = {
  submitted:         'Submitted',
  acknowledged:      'Acknowledged',
  inProgress:        'In Progress',
  awaitingRequester: 'Awaiting Requester Response',
  pendingReview:     'Pending Review',
  completed:         'Completed',
  denied:            'Denied',
  withdrawn:         'Withdrawn',
};

const PRIORITY_LABELS: Record<string, string> = {
  normal: 'Normal',
  high:   'High',
  urgent: 'Urgent',
};

const CHANNEL_LABELS: Record<string, string> = {
  phone:    'Phone',
  email:    'Email',
  mail:     'Mail / Letter',
  inPerson: 'In Person',
  portal:   'Portal',
};

function formatFieldValue(fieldId: string, raw: unknown): string {
  const val = String(raw ?? '');
  if (fieldId === F.status)    return STATUS_LABELS[val]   ?? val;
  if (fieldId === F.priority)  return PRIORITY_LABELS[val] ?? val;
  if (fieldId === F.requestChannel) return CHANNEL_LABELS[val] ?? val;
  if (!val || val === 'undefined' || val === 'null') return '(cleared)';
  // Truncate long text values
  return val.length > 80 ? val.slice(0, 77) + '…' : val;
}

const ACTION_COLORS: Record<string, string> = {
  'request.created':       'var(--color-primary-700)',
  'request.acknowledged':  'var(--cds-green-700)',
  'request.assigned':      '#0284C7',
  'request.updated':       'var(--color-neutral-500)',
  'request.closed':        'var(--color-neutral-800)',
  'clock.paused':          'var(--cds-yellow-500)',
  'clock.resumed':         'var(--cds-green-700)',
  'clock.extended':        'var(--cds-yellow-600)',
  'task.created':          'var(--cds-violet-700)',
  'task.updated':          'var(--cds-violet-700)',
  'task.completed':        'var(--cds-green-700)',
  'document.uploaded':     '#0284C7',
  'document.classified':   '#0284C7',
  'document.released':     'var(--cds-green-700)',
  'redaction.created':     'var(--cds-purple-600)',
  'redaction.approved':    'var(--cds-green-700)',
  'redaction.rejected':    'var(--cds-red-700)',
  'correspondence.sent':   '#0284C7',
  'correspondence.received': 'var(--cds-green-700)',
  'invoice.created':       'var(--cds-yellow-600)',
  'invoice.sent':          'var(--cds-yellow-600)',
  'invoice.paid':          'var(--cds-green-700)',
};

function parseDetails(details?: string): Record<string, unknown> | null {
  if (!details) return null;
  try { return JSON.parse(details); } catch { return null; }
}

function renderDetails(action: string, details?: string): React.ReactNode {
  const d = parseDetails(details);
  if (!d) return null;

  if (action === 'request.updated') {
    // Map opaque field IDs to human-readable change lines
    const changes: { label: string; value: string }[] = [];
    for (const [fieldId, raw] of Object.entries(d)) {
      const label = FIELD_LABELS[fieldId];
      if (!label) continue; // skip internal/unknown fields
      changes.push({ label, value: formatFieldValue(fieldId, raw) });
    }
    if (!changes.length) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2 }}>
        {changes.map(({ label, value }) => (
          <span key={label} style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>
            <span style={{ fontWeight: 500 }}>{label}</span>
            <span style={{ color: 'var(--color-neutral-400)', margin: '0 4px' }}>→</span>
            <span>{value}</span>
          </span>
        ))}
      </div>
    );
  }

  if (action.startsWith('task.')) {
    return d.title
      ? <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{String(d.title)}</span>
      : null;
  }
  if (action === 'correspondence.sent') {
    return d.subject
      ? <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>"{String(d.subject)}"</span>
      : null;
  }
  if (action.startsWith('document.')) {
    return d.fileName
      ? <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{String(d.fileName)}</span>
      : null;
  }
  if (action === 'clock.extended') {
    const parts: string[] = [];
    if (d.days)        parts.push(`+${d.days} days`);
    if (d.newDeadline) parts.push(`→ ${d.newDeadline}`);
    if (d.reason)      parts.push(String(d.reason));
    if (d.notes)       parts.push(String(d.notes));
    return parts.length
      ? <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{parts.join(' · ')}</span>
      : null;
  }
  if (action.startsWith('clock.')) {
    const parts: string[] = [];
    if (d.reason) parts.push(String(d.reason));
    if (d.notes)  parts.push(String(d.notes));
    return parts.length
      ? <span style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}>{parts.join(' · ')}</span>
      : null;
  }
  return null;
}

// ── Mini Calendar ─────────────────────────────────────────────

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const DOW = ['Su','Mo','Tu','We','Th','Fr','Sa'];

interface CalendarProps {
  eventDates: Set<string>; // 'YYYY-MM-DD' strings
  onSelectDate: (date: string | null) => void;
  selectedDate: string | null;
}

function MiniCalendar({ eventDates, onSelectDate, selectedDate }: CalendarProps) {
  const today = new Date();

  // Find the most recent month that has events, or use today
  const latestEventDate = [...eventDates].sort().reverse()[0];
  const initialDate = latestEventDate ? new Date(latestEventDate + 'T12:00:00') : today;

  const [year, setYear]   = useState(initialDate.getFullYear());
  const [month, setMonth] = useState(initialDate.getMonth());

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else              setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else               setMonth(m => m + 1);
  }

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  return (
    <div style={{ userSelect: 'none' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button
          onClick={prevMonth}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--color-neutral-500)', borderRadius: 4 }}
        >
          <IconChevronLeft size={14} />
        </button>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-700)' }}>
          {MONTH_NAMES[month]} {year}
        </span>
        <button
          onClick={nextMonth}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: 'var(--color-neutral-500)', borderRadius: 4 }}
        >
          <IconChevronRight size={14} />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px 0', marginBottom: 4 }}>
        {DOW.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--color-neutral-400)', padding: '2px 0' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Date cells */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, idx) => {
          if (!day) return <div key={`e${idx}`} />;

          const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const hasEvent   = eventDates.has(dateStr);
          const isToday    = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;

          return (
            <button
              key={dateStr}
              onClick={() => onSelectDate(isSelected ? null : dateStr)}
              style={{
                background: isSelected
                  ? 'var(--color-primary-600)'
                  : isToday ? 'var(--color-primary-50)' : 'transparent',
                color: isSelected
                  ? 'white'
                  : isToday ? 'var(--color-primary-700)' : 'var(--color-neutral-700)',
                border: isToday && !isSelected ? '1px solid var(--color-primary-200)' : '1px solid transparent',
                borderRadius: 4,
                cursor: hasEvent ? 'pointer' : 'default',
                padding: '4px 0 2px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                fontSize: 11,
                fontWeight: hasEvent ? 600 : 400,
                opacity: hasEvent ? 1 : 0.4,
              }}
            >
              {day}
              {hasEvent && (
                <div style={{
                  width: 4, height: 4, borderRadius: '50%',
                  background: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--color-primary-500)',
                }} />
              )}
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--color-neutral-100)', fontSize: 11, color: 'var(--color-neutral-500)', textAlign: 'center' }}>
          Showing events for {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
          <button
            onClick={() => onSelectDate(null)}
            style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary-600)', fontSize: 11 }}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────

export default function TimelineTab({ requestId }: Props) {
  const [entries, setEntries]       = useState<AuditEntry[]>([]);
  const [loading, setLoading]       = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const entryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    setLoading(true);
    listAuditLog(requestId)
      .then(setEntries)
      .finally(() => setLoading(false));
  }, [requestId]);

  // Build set of dates that have events
  const eventDates = new Set(
    entries
      .filter(e => e.timestamp)
      .map(e => e.timestamp!.slice(0, 10)),
  );

  // Filter entries by selected date
  const visibleEntries = selectedDate
    ? entries.filter(e => e.timestamp?.startsWith(selectedDate))
    : entries;

  function handleSelectDate(date: string | null) {
    setSelectedDate(date);
    if (date) {
      // Scroll to first matching entry after state update
      setTimeout(() => {
        const firstKey = Object.keys(entryRefs.current).find(k => k.startsWith(date));
        if (firstKey) entryRefs.current[firstKey]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

      {/* ── Timeline card (left) ── */}
      <div className="card" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Card header */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
            Timeline
          </span>
          {selectedDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-neutral-500)' }}>
              <span>
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
              <button
                onClick={() => handleSelectDate(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary-600)', fontSize: 12, padding: 0 }}
              >
                Clear filter
              </button>
            </div>
          )}
          {loading && <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-neutral-400)' }} />}
        </div>

        {/* Scrollable timeline body */}
        <div style={{ padding: '16px', overflowY: 'auto', maxHeight: 480 }}>
          {loading && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-neutral-400)' }}>
              <IconLoader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}
          {!loading && entries.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 14 }}>
              No audit events recorded yet.
            </div>
          )}
          {!loading && entries.length > 0 && visibleEntries.length === 0 && (
            <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--color-neutral-400)', fontSize: 13 }}>
              No events on this date.
            </div>
          )}
          {visibleEntries.map((entry, i) => {
            const color   = ACTION_COLORS[entry.action] ?? 'var(--color-neutral-500)';
            const label   = ACTION_LABELS[entry.action] ?? entry.action;
            const detail  = renderDetails(entry.action, entry.details);
            const ts      = entry.timestamp ? new Date(entry.timestamp) : null;
            const dateKey = entry.timestamp ? `${entry.timestamp.slice(0,10)}-${entry.recordId}` : String(entry.recordId);

            return (
              <div
                key={entry.recordId}
                ref={el => { entryRefs.current[dateKey] = el; }}
                style={{ display: 'flex', gap: 16 }}
              >
                {/* Dot + line */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18, flexShrink: 0 }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: '50%',
                    background: color, marginTop: 5, flexShrink: 0,
                  }} />
                  {i < visibleEntries.length - 1 && (
                    <div style={{ flex: 1, width: 2, background: 'var(--color-neutral-100)', margin: '4px 0' }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ paddingBottom: 18, flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-neutral-800)' }}>{label}</span>
                  {detail && <div style={{ marginTop: 3 }}>{detail}</div>}
                  <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {entry.userEmail && <span>{entry.userEmail}</span>}
                    {ts && <span>{formatDateTime(ts)}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Calendar card (right) ── */}
      <div className="card" style={{ width: 230, flexShrink: 0 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-neutral-100)' }}>
          <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
            Activity
          </span>
        </div>
        <div style={{ padding: 12 }}>
          <MiniCalendar
            eventDates={eventDates}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
          />
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--color-neutral-400)', textAlign: 'center' }}>
            {eventDates.size} day{eventDates.size !== 1 ? 's' : ''} with activity
          </div>
        </div>
      </div>

    </div>
  );
}
